import React from "react";
import { NavLink } from "react-router-dom";

import { useAnalytics } from "../../../api/grpc/analytics/useAnalytics";
import {
  AnalyticsSubscriptionInfoResponse,
  CustomerRequest,
  DevicesResponse,
  WorkplacesResponse,
} from "../../../api/grpc/analytics/analytics";
import useGrpcQuery from "../../../lib/hooks/useGrpcQuery";
import { BILLING_ROOT_PATH } from "../../../lib/routes/routes";
import { useTimeZoneContext } from "../../../lib/context/TimeZone/TimeZoneContext";
import { useSubscriptionContext } from "../../../lib/context/Subscription/SubscriptionContext";
import { Icon } from "../../shared/Icon/Icon";

import "./RecommendedAddOns.scss";

const formatCount = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

export const RecommendedAddOns = () => {
  const { hasUnlimitedUsers, subscriptionLoading } = useSubscriptionContext();
  const { getDevices, getSubscription, getWorkplaces } = useAnalytics();
  const { timeZone } = useTimeZoneContext();

  const { loading: loadingWorkplaces, data: workplacesData } = useGrpcQuery<
    Omit<CustomerRequest, "customerID">,
    WorkplacesResponse
  >({
    method: getWorkplaces,
    variables: {
      timeZone,
    },
  });
  const { loading: loadingDevices, data: devicesData } = useGrpcQuery<
    Omit<CustomerRequest, "customerID">,
    DevicesResponse
  >({
    method: getDevices,
    variables: {
      timeZone,
    },
  });
  const { loading: loadingSubscription, data: subscriptionData } = useGrpcQuery<
    Omit<CustomerRequest, "customerID">,
    AnalyticsSubscriptionInfoResponse
  >({
    method: getSubscription,
    variables: {
      timeZone,
    },
  });

  const rooms = workplacesData?.rooms || 0;
  const desks = workplacesData?.desks || 0;
  const wayfinders = workplacesData?.wayfinders || 0;
  const deviceCount = devicesData?.devices || 0;
  const assignedDeviceCount = devicesData?.assigned || 0;
  const subscription = subscriptionData?.subscription;
  const loading =
    loadingWorkplaces ||
    loadingDevices ||
    loadingSubscription ||
    subscriptionLoading;
  const bookableSpaces = rooms + desks;
  const roomDeviceGap = Math.max(rooms - assignedDeviceCount, 0);
  const totalLicenses =
    (subscription?.roomNumber || 0) +
    (subscription?.deskNumber || 0) +
    (hasUnlimitedUsers ? 0 : subscription?.userNumber || 0) +
    (subscription?.wayfinderNumber || 0);
  const allocatedLicenses =
    (subscription?.licensedRoomNumber || 0) +
    (subscription?.licensedDeskNumber || 0) +
    (hasUnlimitedUsers ? 0 : subscription?.licensedUserNumber || 0) +
    (subscription?.licensedWayfinderNumber || 0);
  const licenseUsage =
    totalLicenses > 0 ? Math.round((allocatedLicenses / totalLicenses) * 100) : 0;
  const isTrial = subscription?.status?.toLowerCase() === "in_trial";
  const isNearLimit = licenseUsage >= 80;
  const workspaceSummary = loading
    ? "Loading your workspace setup..."
    : `Your workspace has ${formatCount(rooms, "room", "rooms")}, ${formatCount(
        desks,
        "desk",
        "desks"
      )} and ${formatCount(wayfinders, "wayfinder", "wayfinders")}.`;

  const recommendations = [
    {
      title: "GOGET One",
      description:
        roomDeviceGap > 0
          ? `You have ${formatCount(
              rooms,
              "room",
              "rooms"
            )} and ${formatCount(
              assignedDeviceCount,
              "assigned device",
              "assigned devices"
            )}. Add room displays to cover more rooms with booking status.`
          : "Keep room booking visible at the door with dedicated GOGET room displays.",
      label: "Hardware",
      metric:
        roomDeviceGap > 0
          ? `${roomDeviceGap} rooms without assigned devices`
          : `${deviceCount} devices in workspace`,
    },
    {
      title: "Floor maps",
      description:
        bookableSpaces > 0
          ? `Turn your ${formatCount(
              bookableSpaces,
              "bookable space",
              "bookable spaces"
            )} into visual maps for booking, wayfinding and overview screens.`
          : "Prepare visual maps before rolling out booking, wayfinding or overview screens.",
      label: "Setup service",
      metric:
        bookableSpaces > 0
          ? `${bookableSpaces} spaces to visualize`
          : "Map-ready setup",
    },
    {
      title: isTrial || isNearLimit ? "Plan upgrade" : "Contact sales",
      description:
        isTrial
          ? "You are currently in trial. Upgrade before rollout so teams can keep using licensed rooms, desks and users."
          : isNearLimit
          ? `You have allocated ${licenseUsage}% of your subscription capacity. Upgrade before teams hit booking limits.`
          : "Planning a larger rollout? Talk to sales about hardware, maps and add-ons that fit your workspace.",
      label: isTrial ? "Trial" : isNearLimit ? "Near limit" : "Growth",
      metric:
        totalLicenses > 0
          ? `${allocatedLicenses}/${totalLicenses} licenses allocated`
          : "Rollout planning",
    },
  ];

  return (
    <section className="RecommendedAddOns" aria-label="Recommended add-ons">
      <div className="RecommendedAddOns__header">
        <div>
          <span>Recommended add-ons</span>
          <h2>Extend your workspace setup</h2>
          <p>{workspaceSummary}</p>
        </div>
        <NavLink to={BILLING_ROOT_PATH}>
          Browse products
          <Icon icon="arrow-right" />
        </NavLink>
      </div>

      <div className="RecommendedAddOns__items">
        {recommendations.map((item) => (
          <NavLink
            key={item.title}
            to={BILLING_ROOT_PATH}
            className="RecommendedAddOns__item"
          >
            <div className="RecommendedAddOns__itemMeta">
              <span>{item.label}</span>
              <strong>{item.metric}</strong>
            </div>
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </NavLink>
        ))}
      </div>
    </section>
  );
};
